import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Card, CircularProgress } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useTenant from '../../hooks/useTenant';
import useAuth from '../../hooks/useAuth';
import useCapabilities from '../../hooks/useCapabilities';
import { atualizarMeuIdioma } from '../../api/usuarios';
import { listarConcursosAbertos } from '../../api/concursos';
import Step1IdiomaLGPD from './steps/Step1IdiomaLGPD';
import Step2EscolhaConcurso from './steps/Step2EscolhaConcurso';
import Step3Cadastro from './steps/Step3Cadastro';
import Step4Conclusao from './steps/Step4Conclusao';
import AuthLayout from '../../layouts/Auth';
import WizardCard from '../../components/WizardCard';

function CadastroWizard() {
  const { t } = useTranslation();
  const { signOut, user, refreshUser } = useAuth();
  const { refreshBootstrap } = useTenant();
  const { refreshCapabilities } = useCapabilities();

  // Chaves do sessionStorage scopadas por usuário pra não misturar entre contas
  const uid = user?.clerkUserId || user?.id || '';
  const stepKey = `wizard_step_${uid}`;
  const dataKey = `wizard_data_${uid}`;

  // Persiste wizard state no sessionStorage pra não perder ao sair e voltar
  const [activeStep, setActiveStep] = useState(() => {
    try {
      return parseInt(sessionStorage.getItem(stepKey) || '0', 10);
    } catch {
      return 0;
    }
  });
  const [stepData, setStepData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(dataKey);
      return saved ? JSON.parse(saved) : { idioma: 'pt-BR', lgpdAceito: false, concursoId: null, concurso: null };
    } catch {
      return { idioma: 'pt-BR', lgpdAceito: false, concursoId: null, concurso: null };
    }
  });
  const [skipConcursoStep, setSkipConcursoStep] = useState(false);
  // Enquanto true, evita renderizar o step "Escolher Concurso" antes de saber
  // se há apenas 1 (auto-skip) ou múltiplos. Sem isso, o step pisca.
  const [concursosCheckPending, setConcursosCheckPending] = useState(true);

  // Checa se tem só 1 concurso → pula step 2 e auto-seleciona
  const { selectedSlug } = useTenant();
  useEffect(() => {
    if (!selectedSlug) {
      setConcursosCheckPending(false);
      return;
    }
    listarConcursosAbertos()
      .then((res) => {
        const list = res.data?.data ?? res.data;
        const arr = Array.isArray(list) ? list : [];
        if (arr.length === 1) {
          setSkipConcursoStep(true);
          setStepData((prev) => {
            const updated = {
              ...prev,
              concursoId: arr[0].concursoId ?? arr[0].id,
              concurso: arr[0],
            };
            try {
              sessionStorage.setItem(dataKey, JSON.stringify(updated));
            } catch {
              /* ignore */
            }
            return updated;
          });
        }
      })
      .catch(() => {
        /* fallback: mostra step normalmente */
      })
      .finally(() => {
        setConcursosCheckPending(false);
      });
  }, [selectedSlug]);

  const ALL_STEPS = [t('wizard.steps.0'), t('wizard.steps.1'), t('wizard.steps.2'), t('wizard.steps.3')];
  // Se só tem 1 concurso, remove o step "Escolher Concurso" do stepper
  const STEPS = skipConcursoStep ? [ALL_STEPS[0], ALL_STEPS[2], ALL_STEPS[3]] : ALL_STEPS;
  // Mapeia o activeStep real pro índice visual do stepper
  const visualStep = skipConcursoStep ? (activeStep <= 0 ? 0 : activeStep - 1) : activeStep;

  // Se restaurou no step 1 mas só tem 1 concurso, avança automaticamente pro step 2
  useEffect(() => {
    if (skipConcursoStep && activeStep === 1) {
      setActiveStep(2);
      try {
        sessionStorage.setItem(stepKey, '2');
      } catch {
        /* ignore */
      }
    }
  }, [skipConcursoStep, activeStep]);

  const handleStepDataChange = (newData) => {
    setStepData(newData);
    try {
      sessionStorage.setItem(dataKey, JSON.stringify(newData));
    } catch {
      /* ignore */
    }
  };

  // Valida se o passo atual pode avançar
  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return stepData.lgpdAceito;
      case 1:
        return !!stepData.concursoId;
      case 2:
        return false; // Step 3 tem seu próprio botão de submit
      case 3:
        return false; // Step 4 é o último — sem "Próximo"
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (activeStep < ALL_STEPS.length - 1) {
      // Pula step 1 (Escolher Concurso) se só tem 1 concurso
      const next = activeStep === 0 && skipConcursoStep ? 2 : activeStep + 1;
      setActiveStep(next);
      try {
        sessionStorage.setItem(stepKey, String(next));
      } catch {
        /* ignore */
      }
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      // Pula step 1 ao voltar se só tem 1 concurso
      const prev = activeStep === 2 && skipConcursoStep ? 0 : activeStep - 1;
      setActiveStep(prev);
      try {
        sessionStorage.setItem(stepKey, String(prev));
      } catch {
        /* ignore */
      }
    }
  };

  // Callback chamado pelo FormularioProdutor quando cadastro é concluído com sucesso
  const handleCadastroSuccess = async () => {
    atualizarMeuIdioma(stepData.idioma).catch(() => {});
    try {
      sessionStorage.removeItem(stepKey);
      sessionStorage.removeItem(dataKey);
    } catch {
      /* ignore */
    }
    // Aguarda refreshes ANTES de mostrar step 4 — senão os botões "Ir para o painel"
    // redirecionam de volta pro wizard porque needsRegistration/bootstrap.proximo.acao
    // ainda estão stale (App.jsx tem Navigate inline baseado nesses flags).
    try {
      if (refreshUser) await refreshUser();
      if (refreshBootstrap) await refreshBootstrap();
      if (refreshCapabilities) await refreshCapabilities();
    } catch {
      /* ignore — ainda assim avança */
    }
    setActiveStep(3);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <Step1IdiomaLGPD data={stepData} onChange={handleStepDataChange} />;
      case 1:
        return (
          <Step2EscolhaConcurso
            data={stepData}
            onChange={handleStepDataChange}
            onAutoSelected={() => setSkipConcursoStep(true)}
          />
        );
      case 2:
        return <Step3Cadastro onSuccess={handleCadastroSuccess} />;
      case 3:
        return <Step4Conclusao concurso={stepData.concurso} idioma={stepData.idioma} />;
      default:
        return null;
    }
  };

  // No step 3, os botões de navegação ficam ocultos (FormularioProdutor tem seu próprio submit)
  // No step 4 (Conclusão), também ocultamos pois os botões estão dentro do step
  // Step 3 (conclusão): sem botões. Step 2 (cadastro): só Voltar, sem Próximo (tem submit próprio).
  const showNavButtons = activeStep !== 3;
  const showNextButton = activeStep !== 2 && activeStep !== 3;

  return (
    <>
      <Helmet title={t('Producer Registration')} />
      <AuthLayout
        wide
        footer={
          <Button
            variant="text"
            size="small"
            color="inherit"
            onClick={signOut}
            startIcon={<LogoutIcon fontSize="small" />}
            sx={{ color: 'text.secondary' }}
          >
            {t('Logout')}
          </Button>
        }
      >
        {concursosCheckPending ? (
          <Card sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Card>
        ) : (
          <WizardCard
            steps={STEPS}
            activeStep={visualStep}
            actions={
              showNavButtons ? (
                <>
                  {activeStep > 0 ? (
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
                      {t('wizard.back')}
                    </Button>
                  ) : (
                    <Box />
                  )}
                  {showNextButton && (
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleNext}
                      disabled={!canProceed()}
                    >
                      {activeStep === 0 ? t('wizard.continue') : t('wizard.next')}
                    </Button>
                  )}
                </>
              ) : null
            }
          >
            {renderStepContent()}
          </WizardCard>
        )}
      </AuthLayout>
    </>
  );
}

export default CadastroWizard;
